import { MotionType } from "./motionType";
class RigidBodyInner {
    _wasmInstance;
    _ptr;
    _vector3Buffer1;
    _vector3Buffer2;
    _shapeReference;
    _referenceCount;
    _shadowCount;
    constructor(wasmInstance, ptr, shapeReference) {
        this._wasmInstance = new WeakRef(wasmInstance);
        this._ptr = ptr;
        const vector3Buffer1Ptr = wasmInstance.allocateBuffer(3 * 4 /* Constants.A32BytesPerElement */);
        this._vector3Buffer1 = wasmInstance.createTypedArray(Float32Array, vector3Buffer1Ptr, 3);
        const vector3Buffer2Ptr = wasmInstance.allocateBuffer(3 * 4 /* Constants.A32BytesPerElement */);
        this._vector3Buffer2 = wasmInstance.createTypedArray(Float32Array, vector3Buffer2Ptr, 3);
        this._shapeReference = shapeReference;
        shapeReference.addReference();
        this._referenceCount = 0;
        this._shadowCount = 0;
    }
    dispose() {
        if (this._referenceCount > 0) {
            throw new Error("Cannot dispose rigid body while it still has references");
        }
        if (this._shadowCount > 0) {
            throw new Error("Cannot dispose rigid body while it still has shadows");
        }
        if (this._ptr === 0) {
            return;
        }
        const wasmInstance = this._wasmInstance.deref();
        if (wasmInstance !== undefined) {
            wasmInstance.deallocateBuffer(this._vector3Buffer1.array.byteOffset, 3 * 4 /* Constants.A32BytesPerElement */);
            wasmInstance.deallocateBuffer(this._vector3Buffer2.array.byteOffset, 3 * 4 /* Constants.A32BytesPerElement */);
            // this operation is thread-safe because the rigid body is not belong to any physics world
            wasmInstance.destroyRigidBody(this._ptr);
        }
        this._ptr = 0;
        this._shapeReference.removeReference();
        this._shapeReference = null;
    }
    get ptr() {
        return this._ptr;
    }
    get vector3Buffer1() {
        return this._vector3Buffer1;
    }
    get vector3Buffer2() {
        return this._vector3Buffer2;
    }
    addReference() {
        this._referenceCount += 1;
    }
    removeReference() {
        this._referenceCount -= 1;
    }
    get hasReferences() {
        return 0 < this._referenceCount;
    }
    addShadow() {
        this._shadowCount += 1;
    }
    removeShadow() {
        this._shadowCount -= 1;
    }
    get hasShadows() {
        return 0 < this._shadowCount;
    }
    getShapeReference() {
        return this._shapeReference;
    }
    setShapeReference(shape) {
        this._shapeReference.removeReference();
        this._shapeReference = shape;
        shape.addReference();
    }
}
function RigidBodyFinalizer(inner) {
    inner.dispose();
}
const PhysicsRigidBodyRegistryMap = new WeakMap();
/**
 * bullet physics rigid body
 */
export class RigidBody {
    /**
     * @internal
     */
    runtime;
    _motionStatePtr;
    _bufferedMotionStatePtr;
    // save only dynamic body ptr for temporal kinematic
    _worldTransformPtr;
    _kinematicStatePtr;
    _inner;
    _worldReference;
    /**
     * @internal
     */
    impl;
    /**
     * Whether this rigid body is dynamic or not(kinematic or static)
     */
    isDynamic;
    constructor(runtime, info, n) {
        const infoPtr = n !== undefined ? info.getPtr(n) : info.ptr;
        if (infoPtr === 0) {
            throw new Error("Cannot create rigid body with null pointer");
        }
        let shape;
        if (n !== undefined) {
            shape = info.getShape(n);
        }
        else {
            shape = info.shape;
        }
        if (shape === null) {
            throw new Error("Cannot create rigid body with null shape");
        }
        if (shape.runtime !== runtime) {
            throw new Error("Cannot create rigid body with shapes from different runtimes");
        }
        const isDynamic = n !== undefined
            ? info.getMotionType(n) === MotionType.Dynamic
            : info.motionType === MotionType.Dynamic;
        this.runtime = runtime;
        const wasmInstance = runtime.wasmInstance;
        const ptr = wasmInstance.createRigidBody(infoPtr);
        const motionStatePtr = wasmInstance.rigidBodyGetMotionStatePtr(ptr);
        this._motionStatePtr = wasmInstance.createTypedArray(Float32Array, motionStatePtr, 20 /* Constants.MotionStateSizeInFloat32Array */);
        const bufferedMotionStatePtr = wasmInstance.rigidBodyGetBufferedMotionStatePtr(ptr);
        this._bufferedMotionStatePtr = wasmInstance.createTypedArray(Float32Array, bufferedMotionStatePtr, 20 /* Constants.MotionStateSizeInFloat32Array */);
        if (isDynamic) {
            const worldTransformPtr = wasmInstance.rigidBodyGetWorldTransformPtr(ptr);
            this._worldTransformPtr = wasmInstance.createTypedArray(Float32Array, worldTransformPtr, 16 /* Constants.BtTransformSizeInFloat32Array */);
        }
        else {
            this._worldTransformPtr = null;
        }
        const kinematicStatePtr = wasmInstance.rigidBodyGetKinematicStatePtr(ptr);
        this._kinematicStatePtr = wasmInstance.createTypedArray(Uint8Array, kinematicStatePtr, 1);
        this._inner = new RigidBodyInner(runtime.wasmInstance, ptr, shape);
        this._worldReference = null;
        let registry = PhysicsRigidBodyRegistryMap.get(wasmInstance);
        if (registry === undefined) {
            registry = new FinalizationRegistry(RigidBodyFinalizer);
            PhysicsRigidBodyRegistryMap.set(wasmInstance, registry);
        }
        registry.register(this, this._inner, this);
        this.impl = runtime.createRigidBodyImpl();
        this.isDynamic = isDynamic;
    }
    /**
     * Dispose the rigid body
     *
     * rigid body must be removed from the world before disposing
     */
    dispose() {
        if (this._inner.ptr === 0) {
            return;
        }
        this._inner.dispose();
        const registry = PhysicsRigidBodyRegistryMap.get(this.runtime.wasmInstance);
        registry?.unregister(this);
    }
    /**
     * @internal
     */
    get ptr() {
        return this._inner.ptr;
    }
    /**
     * @internal
     */
    addReference() {
        this._inner.addReference();
    }
    /**
     * @internal
     */
    removeReference() {
        this._inner.removeReference();
    }
    /**
     * @internal
     */
    addShadowReference() {
        this._inner.addShadow();
    }
    /**
     * @internal
     */
    removeShadowReference() {
        this._inner.removeShadow();
    }
    /**
     * @internal
     */
    get hasShadows() {
        return this._inner.hasShadows;
    }
    /**
     * @internal
     */
    setWorldReference(worldReference) {
        if (this._worldReference !== null && worldReference !== null) {
            throw new Error("Cannot add rigid body to multiple worlds");
        }
        if (this._worldReference === worldReference) {
            return;
        }
        this._worldReference = worldReference;
        if (this._worldReference !== null) {
            this._inner.addReference();
        }
        else {
            this._inner.removeReference();
        }
    }
    /**
     * @internal
     */
    getWorldReference() {
        return this._worldReference;
    }
    /**
     * @internal
     */
    updateBufferedMotionState(forceUseFrontBuffer) {
        this._nullCheck();
        if (forceUseFrontBuffer) {
            const motionStatePtr = this.runtime.wasmInstance.rigidBodyGetMotionStatePtr(this._inner.ptr);
            this._bufferedMotionStatePtr = this.runtime.wasmInstance.createTypedArray(Float32Array, motionStatePtr, 20 /* Constants.MotionStateSizeInFloat32Array */);
        }
        else {
            const bufferedMotionStatePtr = this.runtime.wasmInstance.rigidBodyGetBufferedMotionStatePtr(this._inner.ptr);
            this._bufferedMotionStatePtr = this.runtime.wasmInstance.createTypedArray(Float32Array, bufferedMotionStatePtr, 20 /* Constants.MotionStateSizeInFloat32Array */);
        }
    }
    _nullCheck() {
        if (this._inner.ptr === 0) {
            throw new Error("Cannot access disposed rigid body");
        }
    }
    /**
     * Get the transform matrix of the rigid body
     * @param result The matrix to store the result
     * @returns The transform matrix of the rigid body
     */
    getTransformMatrixToRef(result) {
        this._nullCheck();
        if (this._inner.hasReferences && this.impl.shouldSync) {
            this.runtime.lock.wait();
        }
        const m = this._bufferedMotionStatePtr.array;
        return result.set(m[4 /* MotionStateOffsetsInFloat32Array.MatrixRowX */ + 0], m[8 /* MotionStateOffsetsInFloat32Array.MatrixRowY */ + 0], m[12 /* MotionStateOffsetsInFloat32Array.MatrixRowZ */ + 0], 0, m[4 /* MotionStateOffsetsInFloat32Array.MatrixRowX */ + 1], m[8 /* MotionStateOffsetsInFloat32Array.MatrixRowY */ + 1], m[12 /* MotionStateOffsetsInFloat32Array.MatrixRowZ */ + 1], 0, m[4 /* MotionStateOffsetsInFloat32Array.MatrixRowX */ + 2], m[8 /* MotionStateOffsetsInFloat32Array.MatrixRowY */ + 2], m[12 /* MotionStateOffsetsInFloat32Array.MatrixRowZ */ + 2], 0, m[16 /* MotionStateOffsetsInFloat32Array.Translation */ + 0], m[16 /* MotionStateOffsetsInFloat32Array.Translation */ + 1], m[16 /* MotionStateOffsetsInFloat32Array.Translation */ + 2], 1);
    }
    /**
     * Get the transform matrix of the rigid body
     * @param result The array to store the result
     * @param offset The offset in the array to store the result
     */
    getTransformMatrixToArray(result, offset = 0) {
        this._nullCheck();
        if (this._inner.hasReferences && this.impl.shouldSync) {
            this.runtime.lock.wait();
        }
        const m = this._bufferedMotionStatePtr.array;
        result[offset + 0] = m[4 /* MotionStateOffsetsInFloat32Array.MatrixRowX */ + 0];
        result[offset + 1] = m[8 /* MotionStateOffsetsInFloat32Array.MatrixRowY */ + 0];
        result[offset + 2] = m[12 /* MotionStateOffsetsInFloat32Array.MatrixRowZ */ + 0];
        result[offset + 3] = 0;
        result[offset + 4] = m[4 /* MotionStateOffsetsInFloat32Array.MatrixRowX */ + 1];
        result[offset + 5] = m[8 /* MotionStateOffsetsInFloat32Array.MatrixRowY */ + 1];
        result[offset + 6] = m[12 /* MotionStateOffsetsInFloat32Array.MatrixRowZ */ + 1];
        result[offset + 7] = 0;
        result[offset + 8] = m[4 /* MotionStateOffsetsInFloat32Array.MatrixRowX */ + 2];
        result[offset + 9] = m[8 /* MotionStateOffsetsInFloat32Array.MatrixRowY */ + 2];
        result[offset + 10] = m[12 /* MotionStateOffsetsInFloat32Array.MatrixRowZ */ + 2];
        result[offset + 11] = 0;
        result[offset + 12] = m[16 /* MotionStateOffsetsInFloat32Array.Translation */ + 0];
        result[offset + 13] = m[16 /* MotionStateOffsetsInFloat32Array.Translation */ + 1];
        result[offset + 14] = m[16 /* MotionStateOffsetsInFloat32Array.Translation */ + 2];
        result[offset + 15] = 1;
    }
    /**
     * Set the transform matrix of the rigid body
     *
     * This method will work only if the rigid body motion type is kinematic
     *
     * Application can be deferred to the next frame when world evaluating the rigid body
     * @param matrix The transform matrix to set
     */
    setTransformMatrix(matrix) {
        this.setTransformMatrixFromArray(matrix.m, 0);
    }
    /**
     * Set the transform matrix of the rigid body
     *
     * This method will work only if the rigid body motion type is kinematic
     *
     * Application can be deferred to the next frame when world evaluating the rigid body
     * @param array The array to set the transform matrix from
     * @param offset The offset in the array to set the transform matrix from
     */
    setTransformMatrixFromArray(array, offset = 0) {
        this._nullCheck();
        if (this._inner.hasReferences && this.impl.shouldSync) {
            this.runtime.lock.wait();
        }
        this.impl.setTransformMatrixFromArray(this._motionStatePtr, this._kinematicStatePtr, array, offset);
    }
    /**
     * Set the dynamic transform matrix of the rigid body
     *
     * This method will work only if the rigid body motion type is dynamic
     *
     * Application can be deferred to the next frame when world evaluating the rigid body
     * @param matrix The transform matrix to set
     * @param fallbackToSetTransformMatrix Whether to fallback to setTransformMatrix if the rigid body is not dynamic
     */
    setDynamicTransformMatrix(matrix, fallbackToSetTransformMatrix = false) {
        this.setDynamicTransformMatrixFromArray(matrix.m, 0, fallbackToSetTransformMatrix);
    }
    /**
     * Set the dynamic transform matrix of the rigid body
     *
     * This method will work only if the rigid body motion type is dynamic
     *
     * Application can be deferred to the next frame when world evaluating the rigid body
     * @param array The array to set the transform matrix from
     * @param offset The offset in the array to set the transform matrix from
     * @param fallbackToSetTransformMatrix Whether to fallback to setTransformMatrix if the rigid body is not dynamic
     */
    setDynamicTransformMatrixFromArray(array, offset = 0, fallbackToSetTransformMatrix = false) {
        if (this._worldTransformPtr === null) {
            if (fallbackToSetTransformMatrix) {
                this.setTransformMatrixFromArray(array, offset);
                return;
            }
            else {
                throw new Error("Cannot set dynamic transform of non-dynamic body");
            }
        }
        this._nullCheck();
        if (this._inner.hasReferences && this.impl.shouldSync) {
            this.runtime.lock.wait();
        }
        this.impl.setDynamicTransformMatrixFromArray(this._worldTransformPtr, array, offset);
    }
    /**
     * The effective kinematic state of the rigid body
     *
     * Even rigid body motion type is dynamic,
     * You can set effective kinematic state to true to make it kinematic
     *
     * Application can be deferred to the next frame when world evaluating the rigid body
     */
    get effectiveKinematicState() {
        if (this._worldTransformPtr === null) {
            return true; // non-dynamic body is always kinematic or static
        }
        this._nullCheck();
        if (this._inner.hasReferences && this.impl.shouldSync) {
            this.runtime.lock.wait();
        }
        return this.impl.getEffectiveKinematicState(this._kinematicStatePtr);
    }
    set effectiveKinematicState(value) {
        if (this._worldTransformPtr === null) {
            throw new Error("Cannot set effective kinematic state of non-dynamic body");
        }
        this._nullCheck();
        if (this._inner.hasReferences && this.impl.shouldSync) {
            this.runtime.lock.wait();
        }
        this.impl.setEffectiveKinematicState(this._kinematicStatePtr, value);
    }
    /**
     * Set the linear and angular damping of the rigid body
     *
     * Application can be deferred to the next frame when world evaluating the rigid body
     * @param linearDamping
     * @param angularDamping
     */
    setDamping(linearDamping, angularDamping) {
        this._nullCheck();
        if (this._inner.hasReferences && this.impl.shouldSync) {
            this.runtime.lock.wait();
        }
        this.impl.setDamping(this.runtime.wasmInstance, this._inner.ptr, linearDamping, angularDamping);
    }
    /**
     * Get the linear damping of the rigid body
     * @returns The linear damping of the rigid body
     */
    getLinearDamping() {
        this._nullCheck();
        // does not need to synchronization because damping is not changed by physics simulation
        return this.impl.getLinearDamping(this.runtime.wasmInstance, this._inner.ptr);
    }
    /**
     * Get the angular damping of the rigid body
     * @returns The angular damping of the rigid body
     */
    getAngularDamping() {
        this._nullCheck();
        // does not need to synchronization because damping is not changed by physics simulation
        return this.impl.getAngularDamping(this.runtime.wasmInstance, this._inner.ptr);
    }
    /**
     * Set the mass and local inertia of the rigid body
     *
     * Application can be deferred to the next frame when world evaluating the rigid body
     * @param mass The mass of the rigid body
     * @param localInertia The local inertia of the rigid body
     */
    setMassProps(mass, localInertia) {
        this._nullCheck();
        if (this._inner.hasReferences && this.impl.shouldSync) {
            this.runtime.lock.wait();
        }
        this.impl.setMassProps(this.runtime.wasmInstance, this._inner.ptr, mass, localInertia);
    }
    /**
     * Get the mass of the rigid body
     * @returns The mass of the rigid body
     */
    getMass() {
        this._nullCheck();
        // does not need to synchronization because mass is not changed by physics simulation
        return this.impl.getMass(this.runtime.wasmInstance, this._inner.ptr);
    }
    /**
     * Get the local inertia of the rigid body
     * @returns The local inertia of the rigid body
     */
    getLocalInertia() {
        this._nullCheck();
        // does not need to synchronization because local inertia is not changed by physics simulation
        return this.impl.getLocalInertia(this.runtime.wasmInstance, this._inner.ptr);
    }
    /**
     * Translate the rigid body
     *
     * Application can be deferred to the next frame when world evaluating the rigid body
     * @param translation The translation vector
     */
    translate(translation) {
        this._nullCheck();
        if (this._inner.hasReferences && this.impl.shouldSync) {
            this.runtime.lock.wait();
        }
        this.impl.translate(this.runtime.wasmInstance, this._inner.ptr, translation);
    }
    /**
     * @internal
     */
    get needToCommit() {
        return this.impl.needToCommit ?? false;
    }
    /**
     * @internal
     */
    commitToWasm() {
        if (this.impl.commitToWasm === undefined) {
            throw new Error("commit only avalible on buffered evaluation mode");
        }
        this._nullCheck();
        this.runtime.lock.wait();
        this.impl.commitToWasm(this.runtime.wasmInstance, this._inner.ptr, this._motionStatePtr, this._kinematicStatePtr, this._worldTransformPtr);
    }
    // these methods need to be always synchronized
    /**
     * Get the total force of the rigid body
     *
     * This operation is always synchronized
     * @param result The vector to store the result
     * @returns The total force of the rigid body
     */
    getTotalForceToRef(result) {
        this._nullCheck();
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        const vector3Buffer1 = this._inner.vector3Buffer1.array;
        this.runtime.wasmInstance.rigidBodyGetTotalForce(this._inner.ptr, vector3Buffer1.byteOffset);
        return result.set(vector3Buffer1[0], vector3Buffer1[1], vector3Buffer1[2]);
    }
    /**
     * Get the total torque of the rigid body
     *
     * This operation is always synchronized
     * @param result The vector to store the result
     * @returns The total torque of the rigid body
     */
    getTotalTorqueToRef(result) {
        this._nullCheck();
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        const vector3Buffer1 = this._inner.vector3Buffer1.array;
        this.runtime.wasmInstance.rigidBodyGetTotalTorque(this._inner.ptr, vector3Buffer1.byteOffset);
        return result.set(vector3Buffer1[0], vector3Buffer1[1], vector3Buffer1[2]);
    }
    /**
     * Apply a central force to the rigid body
     *
     * This operation is always synchronized
     * @param force The force vector to apply
     */
    applyCentralForce(force) {
        this._nullCheck();
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        this.runtime.wasmInstance.rigidBodyApplyCentralForce(this._inner.ptr, force.x, force.y, force.z);
    }
    /**
     * Apply a torque to the rigid body
     *
     * This operation is always synchronized
     * @param torque The torque vector to apply
     */
    applyTorque(torque) {
        this._nullCheck();
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        this.runtime.wasmInstance.rigidBodyApplyTorque(this._inner.ptr, torque.x, torque.y, torque.z);
    }
    /**
     * Apply a force to the rigid body
     *
     * This operation is always synchronized
     * @param force The force vector to apply
     * @param relativePosition The relative position vector to apply the force at
     */
    applyForce(force, relativePosition) {
        this._nullCheck();
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        const vector3Buffer1 = this._inner.vector3Buffer1.array;
        vector3Buffer1[0] = force.x;
        vector3Buffer1[1] = force.y;
        vector3Buffer1[2] = force.z;
        const vector3Buffer2 = this._inner.vector3Buffer2.array;
        vector3Buffer2[0] = relativePosition.x;
        vector3Buffer2[1] = relativePosition.y;
        vector3Buffer2[2] = relativePosition.z;
        this.runtime.wasmInstance.rigidBodyApplyForce(this._inner.ptr, vector3Buffer1.byteOffset, vector3Buffer2.byteOffset);
    }
    /**
     * Apply a central impulse to the rigid body
     *
     * This operation is always synchronized
     * @param impulse The impulse vector to apply
     */
    applyCentralImpulse(impulse) {
        this._nullCheck();
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        this.runtime.wasmInstance.rigidBodyApplyCentralImpulse(this._inner.ptr, impulse.x, impulse.y, impulse.z);
    }
    /**
     * Apply a torque impulse to the rigid body
     *
     * This operation is always synchronized
     * @param impulse The impulse vector to apply
     */
    applyTorqueImpulse(impulse) {
        this._nullCheck();
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        this.runtime.wasmInstance.rigidBodyApplyTorqueImpulse(this._inner.ptr, impulse.x, impulse.y, impulse.z);
    }
    /**
     * Apply an impulse to the rigid body
     *
     * This operation is always synchronized
     * @param impulse The impulse vector to apply
     * @param relativePosition The relative position vector to apply the impulse at
     */
    applyImpulse(impulse, relativePosition) {
        this._nullCheck();
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        const vector3Buffer1 = this._inner.vector3Buffer1.array;
        vector3Buffer1[0] = impulse.x;
        vector3Buffer1[1] = impulse.y;
        vector3Buffer1[2] = impulse.z;
        const vector3Buffer2 = this._inner.vector3Buffer2.array;
        vector3Buffer2[0] = relativePosition.x;
        vector3Buffer2[1] = relativePosition.y;
        vector3Buffer2[2] = relativePosition.z;
        this.runtime.wasmInstance.rigidBodyApplyImpulse(this._inner.ptr, vector3Buffer1.byteOffset, vector3Buffer2.byteOffset);
    }
    /**
     * Apply a push impulse to the rigid body
     *
     * This operation is always synchronized
     * @param impulse The impulse vector to apply
     * @param relativePosition The relative position vector to apply the impulse at
     */
    applyPushImpulse(impulse, relativePosition) {
        this._nullCheck();
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        const vector3Buffer1 = this._inner.vector3Buffer1.array;
        vector3Buffer1[0] = impulse.x;
        vector3Buffer1[1] = impulse.y;
        vector3Buffer1[2] = impulse.z;
        const vector3Buffer2 = this._inner.vector3Buffer2.array;
        vector3Buffer2[0] = relativePosition.x;
        vector3Buffer2[1] = relativePosition.y;
        vector3Buffer2[2] = relativePosition.z;
        this.runtime.wasmInstance.rigidBodyApplyPushImpulse(this._inner.ptr, vector3Buffer1.byteOffset, vector3Buffer2.byteOffset);
    }
    /**
     * Get the push velocity of the rigid body
     *
     * This operation is always synchronized
     * @param result The vector to store the result
     * @returns The push velocity of the rigid body
     */
    getPushVelocityToRef(result) {
        this._nullCheck();
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        const vector3Buffer1 = this._inner.vector3Buffer1.array;
        this.runtime.wasmInstance.rigidBodyGetPushVelocity(this._inner.ptr, vector3Buffer1.byteOffset);
        return result.set(vector3Buffer1[0], vector3Buffer1[1], vector3Buffer1[2]);
    }
    /**
     * Get the turn velocity of the rigid body
     *
     * This operation is always synchronized
     * @param result The vector to store the result
     * @returns The turn velocity of the rigid body
     */
    getTurnVelocityToRef(result) {
        this._nullCheck();
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        const vector3Buffer1 = this._inner.vector3Buffer1.array;
        this.runtime.wasmInstance.rigidBodyGetTurnVelocity(this._inner.ptr, vector3Buffer1.byteOffset);
        return result.set(vector3Buffer1[0], vector3Buffer1[1], vector3Buffer1[2]);
    }
    /**
     * Set the push velocity of the rigid body
     *
     * This operation is always synchronized
     * @param velocity The velocity vector to set
     */
    setPushVelocity(velocity) {
        this._nullCheck();
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        this.runtime.wasmInstance.rigidBodySetPushVelocity(this._inner.ptr, velocity.x, velocity.y, velocity.z);
    }
    /**
     * Set the turn velocity of the rigid body
     *
     * This operation is always synchronized
     * @param velocity The velocity vector to set
     */
    setTurnVelocity(velocity) {
        this._nullCheck();
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        this.runtime.wasmInstance.rigidBodySetTurnVelocity(this._inner.ptr, velocity.x, velocity.y, velocity.z);
    }
    /**
     * Apply a central push impulse to the rigid body
     *
     * This operation is always synchronized
     * @param impulse The impulse vector to apply
     */
    applyCentralPushImpulse(impulse) {
        this._nullCheck();
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        this.runtime.wasmInstance.rigidBodyApplyCentralPushImpulse(this._inner.ptr, impulse.x, impulse.y, impulse.z);
    }
    /**
     * Apply a torque turn impulse to the rigid body
     *
     * This operation is always synchronized
     * @param impulse The impulse vector to apply
     */
    applyTorqueTurnImpulse(impulse) {
        this._nullCheck();
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        this.runtime.wasmInstance.rigidBodyApplyTorqueTurnImpulse(this._inner.ptr, impulse.x, impulse.y, impulse.z);
    }
    /**
     * Clear the forces of the rigid body
     *
     * This operation is always synchronized
     */
    clearForces() {
        this._nullCheck();
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        this.runtime.wasmInstance.rigidBodyClearForces(this._inner.ptr);
    }
    /**
     * Get the linear velocity of the rigid body
     *
     * This operation is always synchronized
     * @param result The vector to store the result
     * @returns The linear velocity of the rigid body
     */
    getLinearVelocityToRef(result) {
        this._nullCheck();
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        const vector3Buffer1 = this._inner.vector3Buffer1.array;
        this.runtime.wasmInstance.rigidBodyGetLinearVelocity(this._inner.ptr, vector3Buffer1.byteOffset);
        return result.set(vector3Buffer1[0], vector3Buffer1[1], vector3Buffer1[2]);
    }
    /**
     * Get the angular velocity of the rigid body
     *
     * This operation is always synchronized
     * @param result The vector to store the result
     * @returns The angular velocity of the rigid body
     */
    getAngularVelocityToRef(result) {
        this._nullCheck();
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        const vector3Buffer1 = this._inner.vector3Buffer1.array;
        this.runtime.wasmInstance.rigidBodyGetAngularVelocity(this._inner.ptr, vector3Buffer1.byteOffset);
        return result.set(vector3Buffer1[0], vector3Buffer1[1], vector3Buffer1[2]);
    }
    /**
     * Set the linear velocity of the rigid body
     *
     * This operation is synchronized when `synced` is true.
     *
     * If `synced` is false, the operation will not wait for the lock and
     * will not be applied until the next frame when the world is evaluated.
     * @param velocity The linear velocity vector
     * @param shouldSynced Whether to synchronize the operation
     */
    setLinearVelocity(velocity, shouldSynced) {
        this._nullCheck();
        if (shouldSynced || this.impl.setLinearVelocity === undefined) {
            if (this._inner.hasReferences) {
                this.runtime.lock.wait();
            }
            this.runtime.wasmInstance.rigidBodySetLinearVelocity(this._inner.ptr, velocity.x, velocity.y, velocity.z);
        }
        else {
            if (this._inner.hasReferences && this.impl.shouldSync) {
                this.runtime.lock.wait();
            }
            this.impl.setLinearVelocity(velocity);
        }
    }
    /**
     * Set the angular velocity of the rigid body
     *
     * This operation is synchronized when `synced` is true.
     *
     * If `synced` isd false, the operation will not wait for the lock and
     * will not be applied until the next frame when the world is evaluate.
     * @param velocity The velocity vector to set
     * @param shouldSynced Whether to synchronize the operation
     */
    setAngularVelocity(velocity, shouldSynced) {
        this._nullCheck();
        if (shouldSynced || this.impl.setAngularVelocity === undefined) {
            if (this._inner.hasReferences) {
                this.runtime.lock.wait();
            }
            this.runtime.wasmInstance.rigidBodySetAngularVelocity(this._inner.ptr, velocity.x, velocity.y, velocity.z);
        }
        else {
            if (this._inner.hasReferences && this.impl.shouldSync) {
                this.runtime.lock.wait();
            }
            this.impl.setAngularVelocity(velocity);
        }
    }
    /**
     * Get the velocity of the rigid body in local point
     *
     * This operation is always synchronized
     * @param relativePosition The relative position vector to get the velocity at
     * @param result The vector to store the result
     * @returns The velocity of the rigid body in local point
     */
    getVelocityInLocalPointToRef(relativePosition, result) {
        this._nullCheck();
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        const vector3Buffer1 = this._inner.vector3Buffer1.array;
        const vector3Buffer2 = this._inner.vector3Buffer2.array;
        vector3Buffer1[0] = relativePosition.x;
        vector3Buffer1[1] = relativePosition.y;
        vector3Buffer1[2] = relativePosition.z;
        this.runtime.wasmInstance.rigidBodyGetVelocityInLocalPoint(this._inner.ptr, vector3Buffer1.byteOffset, vector3Buffer2.byteOffset);
        return result.set(vector3Buffer2[0], vector3Buffer2[1], vector3Buffer2[2]);
    }
    /**
     * Get the push velocity of the rigid body in local point
     *
     * This operation is always synchronized
     * @param relativePosition The relative position vector to get the push velocity at
     * @param result The vector to store the result
     * @returns The push velocity of the rigid body in local point
     */
    getPushVelocityInLocalPointToRef(relativePosition, result) {
        this._nullCheck();
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        const vector3Buffer1 = this._inner.vector3Buffer1.array;
        const vector3Buffer2 = this._inner.vector3Buffer2.array;
        vector3Buffer1[0] = relativePosition.x;
        vector3Buffer1[1] = relativePosition.y;
        vector3Buffer1[2] = relativePosition.z;
        this.runtime.wasmInstance.rigidBodyGetPushVelocityInLocalPoint(this._inner.ptr, vector3Buffer1.byteOffset, vector3Buffer2.byteOffset);
        return result.set(vector3Buffer2[0], vector3Buffer2[1], vector3Buffer2[2]);
    }
    /**
     * Get shape of the rigid body
     * @returns The shape of the rigid body
     */
    getShape() {
        this._nullCheck();
        return this._inner.getShapeReference();
    }
    /**
     * Set shape of the rigid body
     *
     * This operation is always synchronized
     * @param shape The shape to set
     */
    setShape(shape) {
        this._nullCheck();
        if (shape.runtime !== this.runtime) {
            throw new Error("Cannot set shape from different runtime");
        }
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        this._inner.setShapeReference(shape);
        this.runtime.wasmInstance.rigidBodySetShape(this._inner.ptr, shape.ptr);
    }
}
